/* Wishlist drawer behavior.
 *
 * Custom element <wishlist-drawer> rendered once globally by
 * sections/wishlist-drawer.liquid. Opens when any [data-wishlist-drawer-open]
 * element is clicked. Closes on overlay click, close button, or Escape.
 *
 * On open, fetches /apps/api/wishlist/mine and renders items. On empty
 * response, shows the prebuilt empty-state markup.
 *
 * Auth headers come from window.ThemeWishlistSession.getAuthHeaders().
 */
(function () {
  if (window.__themeWishlistDrawerInit) return;
  window.__themeWishlistDrawerInit = true;

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-wishlist-drawer-overlay]');
      this.closeBtn = this.querySelector('[data-wishlist-drawer-close]');
      this.body = this.querySelector('[data-wishlist-drawer-body]');
      this.empty = this.querySelector('[data-wishlist-drawer-empty]');
      this.list = this.querySelector('[data-wishlist-drawer-items]');
      this.countLabel = this.querySelector('[data-wishlist-drawer-count]');

      this.overlay.addEventListener('click', () => this.close());
      this.closeBtn.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });

      // Re-fetch when the cache changes from external mutation
      // (heart toggled on a card while the drawer is open).
      document.addEventListener('wishlist:change', () => {
        if (this.getAttribute('aria-hidden') === 'false') {
          this.refresh();
        }
      });
    }

    open() {
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.refresh();
      // Close any open cart drawer.
      const cart = document.querySelector('cart-drawer');
      if (cart && typeof cart.close === 'function') {
        try { cart.close(); } catch (e) { /* ignore */ }
      }
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    async refresh() {
      const session = window.ThemeWishlistSession || {};
      const apiBase = session.getApiBase ? session.getApiBase() : '/apps/api/wishlist';
      const headers = session.getAuthHeaders ? session.getAuthHeaders() : { 'Content-Type': 'application/json' };

      try {
        const resp = await fetch(apiBase + '/mine', { method: 'GET', headers });
        if (!resp.ok) { this.renderError(resp.status); return; }
        const data = await resp.json();
        this.render(data.items || []);
      } catch (err) {
        this.renderError('network');
      }
    }

    renderError(status) {
      this.list.hidden = true;
      this.empty.hidden = false;
      this.empty.innerHTML = '<p>Could not load your wishlist (' + status + '). Please try again.</p>';
    }

    render(items) {
      if (this.countLabel) this.countLabel.textContent = items.length === 0 ? '' : '(' + items.length + ')';

      if (items.length === 0) {
        this.empty.hidden = false;
        this.list.hidden = true;
        this.list.innerHTML = '';
        return;
      }
      this.empty.hidden = true;
      this.list.hidden = false;
      this.list.innerHTML = '';

      for (const item of items) {
        const li = document.createElement('li');
        li.innerHTML = `
          <a class="wishlist-drawer__item" href="/products/${escape(item.product_handle)}">
            <img class="wishlist-drawer__item-thumb"
                 src="${escape(item.product_image_url)}"
                 alt="${escape(item.product_title)}"
                 loading="lazy"
                 width="64" height="64" />
            <div class="wishlist-drawer__item-meta">
              <div class="wishlist-drawer__item-title">${escape(item.product_title)}</div>
              <div class="wishlist-drawer__item-format">${escape(item.product_format_label || 'Wall Art')}</div>
            </div>
            <button type="button"
                    class="wishlist-drawer__item-remove"
                    data-remove-product-id="${escape(String(item.shopify_product_id))}"
                    aria-label="Remove from wishlist">×</button>
          </a>
        `;
        // Intercept the remove button so it doesn't trigger the anchor.
        const removeBtn = li.querySelector('.wishlist-drawer__item-remove');
        removeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.removeItem(parseInt(removeBtn.dataset.removeProductId, 10), li);
        });
        this.list.appendChild(li);
      }
    }

    async removeItem(productId, liEl) {
      const session = window.ThemeWishlistSession || {};
      const apiBase = session.getApiBase ? session.getApiBase() : '/apps/api/wishlist';
      const headers = session.getAuthHeaders ? session.getAuthHeaders() : { 'Content-Type': 'application/json' };
      try {
        const resp = await fetch(apiBase + '/remove', {
          method: 'POST',
          headers,
          body: JSON.stringify({ shopify_product_id: productId }),
        });
        if (!resp.ok) throw new Error('remove failed');
        // Drop the row from the list immediately (don't wait for refresh).
        liEl.remove();
        if (this.list.children.length === 0) {
          this.empty.hidden = false;
          this.list.hidden = true;
        }
        // Update the cache so cards across the page update visually.
        if (window.ThemeWishlist) {
          window.ThemeWishlist.refreshFromPage && window.ThemeWishlist.refreshFromPage();
        }
        document.dispatchEvent(new CustomEvent('wishlist:change'));
      } catch (err) {
        console.warn('[wishlist-drawer] remove failed', err);
      }
    }
  }

  customElements.define('wishlist-drawer', WishlistDrawer);

  // Open triggers.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wishlist-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    const drawer = document.querySelector('wishlist-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });

  // Show/hide the header count bubble in sync with the wishlist cache.
  document.addEventListener('wishlist:change', (e) => {
    const count = (e.detail && e.detail.count) || 0;
    const bubble = document.querySelector('[data-wishlist-count-bubble]');
    if (bubble) {
      bubble.toggleAttribute('hidden', count === 0);
    }
  });

  // ── Escape HTML attribute / text content ──────────────────────────
  function escape(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
})();
